import {
    Dialog,
    DialogContent,
    Divider,
    Grid
} from '@mui/material';
import { WithStyles, useTheme, withStyles } from '@mui/styles';
import { CancelButtonWhite } from '@tassat/button';
import { TransactionTypeString, orderSideTypeCharMap, transactionTypeCharMap } from '@tassat/common-definitions';
import { ITransactFormValues } from '@tassat/forms/src/TransactForm/types';
import { Asset } from '@tassat/fred-definitions';
import { SecurityCurrencyAssetPair, TransactionHistoryRecord } from '@tassat/fred-extended-definitions';
import {
    Failure as FailureIcon,
    Spinner as SpinnerIcon,
    Success as SuccessIcon
} from '@tassat/icon';
import { capitalizeFirstLetter } from '@tassat/utility';
import React from 'react';

import hourGlass from 'Images/hourglass.svg';
import { handleOnClose } from 'Lib/materialUiHelper';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { useAppSelector } from 'Reducers/store/hooks';
import { ValidationData, ValidationState } from 'Reducers/validation';
import { getSecurityCurrencyPairBySecurityTicker } from 'Selectors/assets';
import { getValidation } from 'Selectors/common';
import {
    TransactionFailed,
    TransactionProcessing,
    TransactionSuccess,
    TransactionTimeout
} from 'SharedComponents/transactions/modals';

import type { ModalAction } from './transactionDetailsModal';
import { ISendFormValues } from '../../containers/client/overlays/send/sendForm/types';
import { IWithdrawalFormValues } from '../../containers/client/overlays/withdrawal/withdrawalForm/types';

export interface TransactionModalProps extends WithStyles<typeof muiStyles> {
    transactionValues:
        ISendFormValues | ITransactFormValues | IWithdrawalFormValues |
        TransactionHistoryRecord;
    actionType?: ModalAction;
    open: boolean;
    onClose: () => void;
    allAssets: Asset[];
}

interface DetermineTransactionBody extends TransactionModalProps {
    assetPair: SecurityCurrencyAssetPair;
    validation: ValidationData;
    isOrder?: string;
}

const muiStyles = (theme: ApplicationMuiTheme) => ({
    root: {
        minWidth: '540px',
        padding: '.5rem'
    },
    headerContainer: {
        position: 'relative' as const,
        height: '10rem',
        backgroundColor: theme?.app?.clientTheme.colors.primaryColorLight ||
            '#009BDF'
    },
    headerIcon: {
        height: '86px',
        width: '86px'
    },
    cancelIcon: {
        position: 'absolute' as const,
        top: 18,
        right: 18
    },
    paperWidthXl: { width: '40%' }
});

const isTransactionHistoryRecord =
    (value: unknown): value is TransactionHistoryRecord => typeof value === 'object' && value !== null && 'RequestId' in value;

const determineHeaderIcon = (props: TransactionModalProps & { validation: ValidationData }, theme: ApplicationMuiTheme) => {
    const {
        classes,
        validation
    } = props;

    if (!validation) return null;

    const {
        pending,
        timeout,
        error
    } = validation;

    if (pending) {
        return <SpinnerIcon className={classes.headerIcon} />;
    } else if (timeout) {
        return (
            <img
                alt='time out symbol'
                src={hourGlass}
            />
        );
    } else if (error) {
        return (
            <FailureIcon
                className={classes.headerIcon}
                iconColor={theme?.app?.applicationColors.failure || '#FA5252'}
            />
        );
    } else {
        return (
            <SuccessIcon
                className={classes.headerIcon}
                iconColor={theme?.app?.applicationColors.successLight || '#7DEC62'}
            />
        );
    }
};

const determineTransactionBody = (props: DetermineTransactionBody) => {
    const {
        actionType,
        transactionValues,
        validation,
        onClose,
        isOrder,
        assetPair,
        allAssets
    } = props;

    if (!validation || !transactionValues) return null;

    const {
        pending,
        timeout,
        error
    } = validation;

    if (pending) {
        let progressText = 'Transaction in progress';
        // Case when data comes from transaction (i.e. Approve/Decline/Cancel)
        if ((transactionValues as TransactionHistoryRecord).RequestId) {
            if (actionType === 'cancel') {
                progressText = 'Cancelation in Progress';
            } else if (actionType === 'approve') {
                progressText = 'Approval in Progress';
            } else if (actionType === 'decline') {
                progressText = 'Decline in Progress';
            } else {
                const transactionType = isOrder ?
                    orderSideTypeCharMap((transactionValues as TransactionHistoryRecord).OrderSide)
                    : transactionTypeCharMap((transactionValues as TransactionHistoryRecord).type);
                progressText = `${capitalizeFirstLetter(transactionType ?? '')} in Progress`;
            }
        // Case when data comes from input forms
        } else if ((transactionValues as ITransactFormValues)?.Type) {
            const transactionType = isOrder ?
                orderSideTypeCharMap((transactionValues as ITransactFormValues).Type)
                : transactionTypeCharMap((transactionValues as ITransactFormValues).Type);
            progressText = `${capitalizeFirstLetter(transactionType ?? '')} in Progress`;
        }

        return (
            <TransactionProcessing
                text={progressText}
            />
        );
    } else if (timeout) {
        return (
            <TransactionTimeout />
        );
    } else if (error) {
        return (
            <TransactionFailed
                onClose={onClose}
                transactionValues={transactionValues}
            />
        );
    } else {
        return (
            <TransactionSuccess
                actionType={actionType}
                allAssets={allAssets}
                assetPair={assetPair}
                onClose={onClose}
                transactionValues={transactionValues}
                validation={validation}
            />
        );
    }
};

export function TransactionModalComponent(props: TransactionModalProps) {
    const {
        classes,
        transactionValues,
        open,
        onClose
    } = props;

    const ticker = isTransactionHistoryRecord(transactionValues) ?
        transactionValues.Ticker
        : ((transactionValues as ISendFormValues)?.AssetType?.refData?.security ||
            (transactionValues as ISendFormValues)?.AssetType?.refData?.currency)?.Ticker;
    const assetPair = useAppSelector((state) => getSecurityCurrencyPairBySecurityTicker(state, ticker));
    const isOrder = transactionValues && orderSideTypeCharMap(
        isTransactionHistoryRecord(transactionValues) ?
            transactionValues.OrderSide
            : (transactionValues as ITransactFormValues).Type
    );

    const validation = useAppSelector((state) => {
        let transactionType = transactionValues &&
            transactionTypeCharMap(
                isOrder ?
                    'o'
                    : isTransactionHistoryRecord(transactionValues) ?
                        transactionValues.type || transactionValues.OrderSide
                        : (transactionValues as ITransactFormValues).Type
            )
                .toLowerCase();
        if (transactionType === TransactionTypeString.transfer.toLowerCase()) {
            transactionType = TransactionTypeString.send.toLowerCase();
        }

        return getValidation(state)[transactionType as keyof ValidationState];
    });

    const theme = useTheme<ApplicationMuiTheme>();
    if (!transactionValues) return null;

    return (
        <Dialog
            classes={{ paperWidthXl: classes.paperWidthXl }}
            disableEscapeKeyDown
            maxWidth='xl'
            onClose={() => handleOnClose(onClose)}
            open={open}
            transitionDuration={{
                enter: 0,
                exit: 0
            }}
        >
            <Grid
                className={classes.root}
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                <Grid
                    container
                    item
                    justifyContent='center'
                    spacing={0}
                    xs={12}
                >
                    <Grid
                        alignItems='center'
                        className={classes.headerContainer}
                        container
                        direction='row'
                        item
                        justifyContent='center'
                        spacing={0}
                        wrap='nowrap'
                        xs={12}
                    >
                        {determineHeaderIcon({
                            ...props,
                            validation
                        }, theme)}
                    </Grid>
                    {validation && !validation.pending &&
                        <Grid
                            className={classes.cancelIcon}
                            item
                        >
                            <CancelButtonWhite onClick={onClose} />
                        </Grid>}
                </Grid>
                <Divider variant='middle' />
                <Grid
                    item
                    xs={12}
                >
                    <DialogContent>
                        {determineTransactionBody({
                            ...props,
                            assetPair,
                            validation,
                            isOrder
                        })}
                    </DialogContent>
                </Grid>
            </Grid>
        </Dialog>
    );
}

const TransactionModal = withStyles(muiStyles)(TransactionModalComponent);
export { TransactionModal };